import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Paper, Box, Card, CardHeader, CardContent, Stack, Table, TableContainer, TableBody, TableRow, TableCell, Typography, Button, CircularProgress } from '@mui/material';
import { Drafts, AdsClick, Refresh, PlayArrow, Stop, Devices } from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import LoadingOverlay from '../../loadingOverlay/LoadingOverlay';
import ActionsChart from '../charts/ActionsChart';
import DevicesChart from '../charts/DevicesChart';
import TypedValue from '../../TypedValue';
import { useTheme } from '@mui/material/styles';
import { lighten } from '@mui/system';
import AlertBar from '../../alertBar/AlertBar';

const chartHeight = 500;

const ActionsBreakdown = ({ type, data, icon }) => {
    const theme = useTheme();

    const sumOfActionsByType = Object.values(
        data.reduce((acc, { type, total }) => {
            if (!acc[type]) {
                acc[type] = { type, total: 0 };
            }

            acc[type].total += total;

            return acc;
        }, {})
    );

    const total = data.reduce((n, { total }) => n + total, 0);

    return (
        <Card>
            <CardHeader
                sx={{
                    m: 0,
                    py: 1
                }}
                slotProps={{
                    title: {
                        variant: 'h6',
                        fontWeight: 'normal'
                    }
                }}
                avatar={icon}
                title={`${type} in last hour`}
            />
            <CardContent
                sx={{
                    px: 1,
                    py: 0,
                    '&:last-child': {
                        paddingBottom: 1
                    },
                    '& .MuiTableRow-root:last-child .MuiTableCell-root': {
                        borderBottom: 0
                    },
                    '& .MuiTableCell-root': {
                        px: 1,
                        py: 0.5
                    }
                }}
            >
                <Stack spacing={1}>
                    <Paper variant="outlined" sx={{ backgroundColor: lighten(theme.palette.primary.main, 0.9) }}>
                        <TableContainer>
                            <Table
                                size="small"
                                padding="none"
                            >
                                <TableBody>
                                    <TableRow>
                                        <TableCell variant="head">
                                            All Channel Total
                                        </TableCell>
                                        <TableCell align="right" width="30%">
                                            <Typography variant="body1" color="primary">
                                                <TypedValue value={total} type="number" />
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                    <Paper variant="outlined">
                        <TableContainer>
                            <Table size="small">
                                <TableBody>
                                    {sumOfActionsByType.map(e => (
                                        <TableRow key={e.type}>
                                            <TableCell variant="head">
                                                {e.type === 'Click' || e.type === 'Open' ? `Email ${e.type}` : e.type}
                                            </TableCell>
                                            <TableCell align="right" width="30%">
                                                <Typography variant="body1" color="primary">
                                                    <TypedValue value={e.total} type="number" />
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Stack>
            </CardContent>
        </Card>
    );
};

const Now = ({ data, onFetchData, pollingActive, onTogglePolling, pollingInterval, refreshDateTime, working, sendDateTime }) => {
    const [isLoading, setIsLoading] = useState(true);

    const actions = data?.actions;
    const devices = data?.devices;

    useEffect(() => {
        actions && setIsLoading(false);
    }, [actions]);

    if (isLoading) {
        return (
            <LoadingOverlay />
        );
    }

    return (
        <>
            <AlertBar
                position="relative"
                action={(
                    <Button
                        onClick={onFetchData}
                        size="small"
                        disabled={working}
                        startIcon={working ? <CircularProgress size={18} color="inherit" /> : <Refresh />}
                    >
                        Refresh Now
                    </Button>
                )}
                secondaryAction={(
                    <Button
                        onClick={() => onTogglePolling(!pollingActive)}
                        size="small"
                        variant="outlined"
                        startIcon={pollingActive ? <Stop /> : <PlayArrow />}
                    >
                        {pollingActive ? 'Stop Auto Refresh' : 'Start Auto Refresh'}
                    </Button>
                )}
            >
                {pollingActive && `Data will refresh every ${pollingInterval / 1000} seconds. `}{refreshDateTime && `Last refreshed ${format((new Date(refreshDateTime)), 'HH:mm:ss')}`}
            </AlertBar>
            <Box sx={{ m: 2 }}>
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <Paper>
                            <ActionsChart actions={actions} chartHeight={chartHeight} sendDateTime={sendDateTime} />
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <ActionsBreakdown
                            type="Opens"
                            data={actions?.filter(e => e.type.toLowerCase().includes('open'))}
                            icon={<Drafts />}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <ActionsBreakdown
                            type="Clicks"
                            data={actions?.filter(e => e.type.toLowerCase().includes('click'))}
                            icon={<AdsClick />}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardHeader
                                sx={{
                                    m: 0,
                                    py: 1,
                                    '& .MuiCardHeader-action': {
                                        alignSelf: 'center'
                                    }
                                }}
                                slotProps={{
                                    title: {
                                        variant: 'h6',
                                        fontWeight: 'normal'
                                    }
                                }}
                                avatar={<Devices />}
                                title="Devices"
                            />
                            <CardContent>
                                <DevicesChart label="Devices" data={devices} />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export default Now;